import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StreamingServiceCombobox } from "@/components/shared/StreamingServiceCombobox";
import {
  streamingAccountService,
  StreamingAccount,
  Slot,
  ESlotStatus,
  EStreamingAccountStatus,
  EStreamingAccountType,
} from "@/services/streaming-account";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { ServiceBadge } from "@/components/dashboard/ServiceBadge";
import { RegisterStreamingAccountModal } from "@/components/streaming-accounts/RegisterStreamingAccountModal";
import { RegisterSlotModal } from "@/components/streaming-accounts/RegisterSlotModal";
import { UpdateSlotModal } from "@/components/streaming-accounts/UpdateSlotModal";
import { AssignSubscriptionModal } from "@/components/subscriptions/AssignSubscriptionModal";
import { TransferSubscriptionModal } from "@/components/subscriptions/TransferSubscriptionModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Plus,
  UserPlus,
  ArrowRightLeft,
  Eye,
  Loader2,
  Search,
  Pencil,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { EownerType } from "@/services/subscription";
import { useUrlFilters, useUrlPage, useUrlSearch } from "@/hooks/use-url-filters";

// Genera números de pagination con elipsis
function generatePaginationNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const delta = 1;
  const range: (number | string)[] = [];

  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      range.push(i);
    } else if (range[range.length - 1] !== '...') {
      range.push('...');
    }
  }

  return range;
}

const PAGE_SIZE = 10;

const DEFAULT_FILTERS = {
  service: "all",
  status: EStreamingAccountStatus.ACTIVE as string,
  type: "all",
};

interface AccountCardProps {
  account: StreamingAccount;
  onAssignSlot: (slot: Slot, account: StreamingAccount) => void;
  onViewAccount: (account: StreamingAccount) => void;
  onTransferSlot: (slot: Slot, account: StreamingAccount) => void;
  onAddSlot: (account: StreamingAccount) => void;
  onEditSlot: (slot: Slot, account: StreamingAccount) => void;
}

function AccountCard({ account, onAssignSlot, onViewAccount, onTransferSlot, onAddSlot, onEditSlot }: AccountCardProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const usedSlots = account.slots.filter(
    (s) => s.status === ESlotStatus.OCCUPIED
  ).length;
  const availableSlots = account.totalSlots - usedSlots;
  const usagePercent = (usedSlots / account.totalSlots) * 100;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="card-elevated overflow-hidden">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-4 lg:p-5 hover:bg-muted/30 transition-colors cursor-pointer">
            <div className="flex items-center gap-4">
              <ServiceBadge icon={account.service.icon} name={account.service.name} />
              <div className="text-left">
                <p className="text-sm text-muted-foreground">
                  {account.email}
                </p>
                {account.type === EStreamingAccountType.DEDICATED &&
                  account.ownerName && (
                    <p className="text-xs text-primary">
                      Dedicada: {account.ownerName}
                    </p>
                  )}
              </div>
            </div>

            <div className="flex items-center gap-6">
              {/* Slot Usage */}
              <div className="hidden sm:block w-40">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Slots</span>
                  <span className="font-medium text-foreground">
                    {usedSlots}/{account.totalSlots}
                  </span>
                </div>
                <Progress value={usagePercent} className="h-2" />
              </div>

              {/* Quick Stats */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="slot-indicator slot-occupied" />
                  <span className="text-sm font-medium text-foreground">
                    {usedSlots}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="slot-indicator slot-available" />
                  <span className="text-sm font-medium text-foreground">
                    {availableSlots}
                  </span>
                </div>
              </div>

              <StatusBadge status={account.status} />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewAccount(account);
                    }}
                  >
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>View account</TooltipContent>
              </Tooltip>

              <div className="p-1">
                {isOpen ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-border bg-muted/20">
            <div className="p-4 lg:p-5 space-y-3">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-foreground">Profile Slots</h4>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddSlot(account);
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Add Slot
                </Button>
              </div>

              <div className="grid gap-3">
                {account.slots.map((slot) => (
                  <div
                    key={slot.id}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg border transition-colors",
                      slot.status === ESlotStatus.AVAILABLE
                        ? "bg-success/5 border-success/20"
                        : "bg-card border-border"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={cn(
                          "slot-indicator",
                          slot.status === ESlotStatus.AVAILABLE
                            ? "slot-available"
                            : "slot-occupied"
                        )}
                      />
                      <div>
                        <p className="font-medium text-foreground">
                          {slot.name}
                        </p>
                        {slot.status === ESlotStatus.OCCUPIED && (
                          <p className="text-sm text-muted-foreground">
                            {slot.client?.name} • Ends{" "}
                            {slot.subscriptionEndDate &&
                              new Date(
                                slot.subscriptionEndDate
                              ).toLocaleDateString()}
                          </p>
                        )}
                        {slot.status === ESlotStatus.AVAILABLE && (
                          <p className="text-sm text-success">
                            Available for assignment
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {slot.price && (
                        <span className="text-sm font-semibold text-foreground mr-2">
                          ${slot.price}/mo
                        </span>
                      )}

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditSlot(slot, account);
                            }}
                          >
                            <Pencil className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Editar slot</TooltipContent>
                      </Tooltip>

                      {slot.status === ESlotStatus.AVAILABLE ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAssignSlot(slot, account);
                              }}
                            >
                              <UserPlus className="h-4 w-4" />
                              Assign
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Assign client to this slot
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log("=== DEBUG VIEW SUBSCRIPTION ===");
                                  console.log("slot:", slot);
                                  console.log("slot.id:", slot.id);
                                  navigate(`/subscriptions/${slot.subscriptionItems[0].subscriptionId}`);
                                }}
                              >
                                <Eye className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>View subscription</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onTransferSlot(slot, account);
                                }}
                              >
                                <ArrowRightLeft className="h-4 w-4 text-primary" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Transferir suscripción</TooltipContent>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

interface AssignSlotData {
  slot: Slot;
  account: StreamingAccount;
}

interface TransferSlotData {
  slot: Slot;
  account: StreamingAccount;
}

export default function Accounts() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery, debouncedSearch] = useUrlSearch("search");
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS);
  const { service: serviceFilter, status: statusFilter, type: typeFilter } = filters;
  const [currentPage, setCurrentPage] = useUrlPage();
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [assignSlotData, setAssignSlotData] = useState<AssignSlotData | null>(null);
  const [transferSlotData, setTransferSlotData] = useState<TransferSlotData | null>(null);
  const [addSlotAccount, setAddSlotAccount] = useState<StreamingAccount | null>(null);
  const [editSlotData, setEditSlotData] = useState<{ slot: Slot; account: StreamingAccount } | null>(null);

  const handleAssignSlot = (slot: Slot, account: StreamingAccount) => {
    setAssignSlotData({ slot, account });
  };

  const handleCloseAssignModal = () => {
    setAssignSlotData(null);
  };

  const handleTransferSlot = (slot: Slot, account: StreamingAccount) => {
    setTransferSlotData({ slot, account });
  };

  const handleCloseTransferModal = () => {
    setTransferSlotData(null);
  };

  const handleAddSlot = (account: StreamingAccount) => {
    setAddSlotAccount(account);
  };

  const handleCloseAddSlotModal = () => {
    setAddSlotAccount(null);
  };

  const handleEditSlot = (slot: Slot, account: StreamingAccount) => {
    setEditSlotData({ slot, account });
  };

  const handleCloseEditSlotModal = () => {
    setEditSlotData(null);
  };

  const handleViewAccount = (account: StreamingAccount) => {
    navigate(`/accounts/${account.id}`);
  };

  // Obtener cuentas de streaming desde el API con pagination y filtros
  const {
    data: accountsResponse,
    isLoading: isLoadingAccounts,
    isFetching,
  } = useQuery({
    queryKey: ["streaming-accounts", currentPage, debouncedSearch, serviceFilter, statusFilter, typeFilter],
    queryFn: () =>
      streamingAccountService.getAll({
        page: currentPage,
        limit: PAGE_SIZE,
        email: debouncedSearch || undefined,
        serviceId: serviceFilter !== "all" ? parseInt(serviceFilter) : undefined,
        status:
          statusFilter !== "all"
            ? (statusFilter as EStreamingAccountStatus)
            : undefined,
        type:
          typeFilter !== "all"
            ? (typeFilter as EStreamingAccountType)
            : undefined,
      }),
  });

  const accounts = accountsResponse?.data || [];
  const pagination = accountsResponse?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.totalItems || 0;

  const totalSlots = accounts.reduce((sum, acc) => sum + acc.totalSlots, 0);
  const usedSlots = accounts.reduce(
    (sum, acc) =>
      sum + acc.slots.filter((s) => s.status === ESlotStatus.OCCUPIED).length,
    0
  );

  return (
    <DashboardLayout
      title="Accounts & Slots"
      subtitle="Manage streaming accounts and profile availability"
    >
      <div className="space-y-6 animate-fade-in">
        {/* Summary Bar */}
        <div className="flex flex-wrap items-center gap-4 p-4 card-elevated">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Total Accounts:
            </span>
            <span className="font-semibold text-foreground">
              {accounts.length}
            </span>
          </div>
          <div className="w-px h-5 bg-border hidden sm:block" />
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Total Slots:</span>
            <span className="font-semibold text-foreground">{totalSlots}</span>
          </div>
          <div className="w-px h-5 bg-border hidden sm:block" />
          <div className="flex items-center gap-2">
            <span className="slot-indicator slot-occupied" />
            <span className="text-sm text-muted-foreground">Used:</span>
            <span className="font-semibold text-foreground">{usedSlots}</span>
          </div>
          <div className="w-px h-5 bg-border hidden sm:block" />
          <div className="flex items-center gap-2">
            <span className="slot-indicator slot-available" />
            <span className="text-sm text-muted-foreground">Available:</span>
            <span className="font-semibold text-success">
              {totalSlots - usedSlots}
            </span>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col gap-4">
          {/* Search Bar */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Buscar por email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <StreamingServiceCombobox
                value={serviceFilter}
                onChange={(value) => setFilters({ service: value })}
              />

              <Select
                value={statusFilter}
                onValueChange={(value) => setFilters({ status: value })}
              >
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value={EStreamingAccountStatus.ACTIVE}>
                    Active
                  </SelectItem>
                  <SelectItem value={EStreamingAccountStatus.INACTIVE}>
                    Inactive
                  </SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={typeFilter}
                onValueChange={(value) => setFilters({ type: value })}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value={EStreamingAccountType.SHARED}>
                    Shared
                  </SelectItem>
                  <SelectItem value={EStreamingAccountType.DEDICATED}>
                    Dedicated
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button className="gap-2" onClick={() => setIsRegisterModalOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Account
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {isLoadingAccounts && (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* Accounts List */}
        {!isLoadingAccounts && (
          <div className="space-y-4 relative">
            {/* Loading overlay for fetching */}
            {isFetching && !isLoadingAccounts && (
              <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10 rounded-lg">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {accounts.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                onAssignSlot={handleAssignSlot}
                onViewAccount={handleViewAccount}
                onTransferSlot={handleTransferSlot}
                onAddSlot={handleAddSlot}
                onEditSlot={handleEditSlot}
              />
            ))}
          </div>
        )}

        {!isLoadingAccounts && accounts.length === 0 && (
          <div className="card-elevated p-8 text-center">
            <p className="text-muted-foreground">No accounts found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Try adjusting your filters or add a new account
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Mostrando {accounts.length} de {totalItems} cuentas
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || isFetching}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {generatePaginationNumbers(currentPage, totalPages).map((page, idx) => (
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">...</span>
                  ) : (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      className="w-9"
                      onClick={() => setCurrentPage(page as number)}
                      disabled={isFetching}
                    >
                      {page}
                    </Button>
                  )
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || isFetching}
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Register Modal */}
      <RegisterStreamingAccountModal
        open={isRegisterModalOpen}
        onOpenChange={setIsRegisterModalOpen}
      />

      {/* Assign Subscription Modal */}
      {assignSlotData && (
        <AssignSubscriptionModal
          open={!!assignSlotData}
          onOpenChange={(open) => !open && handleCloseAssignModal()}
          slotId={assignSlotData.slot.id}
          slotName={assignSlotData.slot.name}
          streamingAccountId={assignSlotData.account.id}
          serviceId={assignSlotData.account.serviceId}
        />
      )}

      {/* Transfer Subscription Modal */}
      {transferSlotData && (
        <TransferSubscriptionModal
          open={!!transferSlotData}
          onOpenChange={(open) => !open && handleCloseTransferModal()}
          subscriptionId={String(transferSlotData.slot.subscriptionItems[0]?.id) || ""}
          currentSlotName={transferSlotData.slot.name}
          currentAccountEmail={transferSlotData.account.email}
          clientName={transferSlotData.slot.client?.name || ""}
          serviceId={transferSlotData.account.serviceId}
        />
      )}

      {/* Register Slot Modal */}
      {addSlotAccount && (
        <RegisterSlotModal
          open={!!addSlotAccount}
          onOpenChange={(open) => !open && handleCloseAddSlotModal()}
          accountId={addSlotAccount.id}
          accountEmail={addSlotAccount.email}
        />
      )}

      {/* Update Slot Modal */}
      {editSlotData && (
        <UpdateSlotModal
          open={!!editSlotData}
          onOpenChange={(open) => !open && handleCloseEditSlotModal()}
          slot={editSlotData.slot}
          accountId={editSlotData.account.id}
        />
      )}
    </DashboardLayout>
  );
}
